import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, KIND, SIZE } from 'baseui/button';
import AuthContext from '../shared/AuthContext';
import { formatLastSeen } from '../../utils/formatLastSeen';
import { ADMIN_FEATURE_MATRIX, ADMIN_QUICK_LINKS } from './adminNav';
import '../pages/AdminUsersPage.css';
import './AdminDashboardPage.css';

const REFRESH_MS = 30_000;

function formatNum(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return v.toLocaleString();
}

export default function AdminDashboardPage() {
  const { apiClient } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await apiClient.get('/api/admin/dashboard', { timeout: 15000 });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(
        e.response?.status === 403
          ? 'Admin access required.'
          : 'Could not load admin dashboard. Restart the API if you just deployed.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiClient]);

  useEffect(() => {
    load(false);
    const id = window.setInterval(() => load(true), REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const metrics = data?.metrics || {};
  const platform = data?.platform || {};
  const onlineUsers = data?.onlineUsers || [];
  const aiLimits = platform.aiLimits || {};

  return (
    <div className="admin-users-page">
        <header className="admin-users-header">
          <div>
            <h1>Overview</h1>
            <p className="admin-users-sub">
              Live snapshot · refreshes every 30s
              {platform.generatedAt ? ` · Updated ${formatLastSeen(platform.generatedAt)}` : ''}
            </p>
          </div>
          <Button
            kind={KIND.secondary}
            size={SIZE.compact}
            isLoading={refreshing}
            onClick={() => load(true)}
          >
            Refresh
          </Button>
        </header>

        {error && (
          <div className="admin-users-error" role="alert">
            {error}
          </div>
        )}

        <div className="admin-dash-grid">
          <div className="admin-kpi-grid" aria-live="polite">
            <div className="admin-kpi admin-kpi--accent">
              <p className="admin-kpi-label">Online now</p>
              <p className="admin-kpi-value">{formatNum(metrics.usersOnline)}</p>
              <p className="admin-kpi-hint">Last {platform.onlineWindowMinutes ?? 5} min</p>
            </div>
            <div className="admin-kpi">
              <p className="admin-kpi-label">Active sessions</p>
              <p className="admin-kpi-value">{formatNum(metrics.activeSessions)}</p>
              <p className="admin-kpi-hint">Refresh tokens</p>
            </div>
            <div className="admin-kpi">
              <p className="admin-kpi-label">Total users</p>
              <p className="admin-kpi-value">{formatNum(metrics.totalUsers)}</p>
              <p className="admin-kpi-hint">{formatNum(metrics.adminCount)} admins</p>
            </div>
            <div className="admin-kpi">
              <p className="admin-kpi-label">Questions</p>
              <p className="admin-kpi-value">{formatNum(metrics.questionCount)}</p>
              <p className="admin-kpi-hint">In bank</p>
            </div>
            <div className="admin-kpi">
              <p className="admin-kpi-label">Restrictions</p>
              <p className="admin-kpi-value">{formatNum(metrics.restrictionsCount)}</p>
              <p className="admin-kpi-hint">Notebook excludes</p>
            </div>
            <div className="admin-kpi">
              <p className="admin-kpi-label">Feedback</p>
              <p className="admin-kpi-value">{formatNum(metrics.feedbackCount)}</p>
              <p className="admin-kpi-hint">Question reports</p>
            </div>
            <div className="admin-kpi">
              <p className="admin-kpi-label">AI today</p>
              <p className="admin-kpi-value">{formatNum(metrics.aiRequestsToday)}</p>
              <p className="admin-kpi-hint">All users</p>
            </div>
            <div className="admin-kpi">
              <p className="admin-kpi-label">Pro / Team</p>
              <p className="admin-kpi-value">
                {formatNum(metrics.proSubscribers)} / {formatNum(metrics.teamSubscribers)}
              </p>
              <p className="admin-kpi-hint">Plans</p>
            </div>
          </div>

          <div className="admin-two-col">
            <div className="admin-panel">
              <div className="admin-panel-head">
                <h2>Currently online</h2>
                <Link to="/admin/users">All users</Link>
              </div>
              <div className="admin-panel-body">
                {loading && onlineUsers.length === 0 ? (
                  <p className="admin-users-muted">Loading…</p>
                ) : onlineUsers.length === 0 ? (
                  <p className="admin-users-muted">No users online in the activity window.</p>
                ) : (
                  <ul className="admin-online-list">
                    {onlineUsers.map((u) => (
                      <li key={u.userId}>
                        <div>
                          <div className="admin-online-name">{u.name}</div>
                          <div className="admin-online-email">{u.email}</div>
                        </div>
                        <span className="admin-users-muted">{formatLastSeen(u.lastSeenAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="admin-panel">
              <div className="admin-panel-head">
                <h2>Quick actions</h2>
              </div>
              <div className="admin-panel-body">
                <div className="admin-platform-tags" style={{ marginBottom: 16 }}>
                  <span className={`admin-tag ${platform.billingDemoMode ? 'admin-tag--warn' : 'admin-tag--ok'}`}>
                    Billing: {platform.billingDemoMode ? 'Demo mode' : 'Live Stripe'}
                  </span>
                  <span className="admin-tag">
                    AI — Free {aiLimits.free ?? 10} · Pro {aiLimits.pro ?? 35} · Team {aiLimits.team ?? 70}/day
                    {aiLimits.geminiMaxPerMinute != null && (
                      <> · Gemini burst {aiLimits.geminiMaxPerMinute}/min</>
                    )}
                  </span>
                </div>
                <div className="admin-module-grid">
                  {ADMIN_QUICK_LINKS.map((link) => (
                    <Link key={link.path} to={link.path} className="admin-module-card">
                      <h3>{link.title}</h3>
                      <p>{link.description}</p>
                    </Link>
                  ))}
                  <Link to="/admin/bulk-upload" className="admin-module-card">
                    <h3>Bulk upload</h3>
                    <p>Import JSON or generate questions with AI.</p>
                  </Link>
                  <Link to="/admin/restrictions" className="admin-module-card">
                    <h3>Restrictions</h3>
                    <p>Notebook restrictions per user.</p>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-panel">
            <div className="admin-panel-head">
              <h2>Admin tools</h2>
              <span className="admin-users-muted">Capabilities from product docs</span>
            </div>
            <div className="admin-panel-body admin-matrix">
              {ADMIN_FEATURE_MATRIX.map((block) => (
                <section key={block.section}>
                  <h3>{block.section}</h3>
                  <ul>
                    {block.items.map((item) => (
                      <li key={`${block.section}-${item.label}`}>
                        <Link to={item.path}>{item.label}</Link>
                        <span>{item.doc}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </div>
    </div>
  );
}
